import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Explicitly disable any global data labels plugin
ChartJS.defaults.plugins.datalabels = { display: false };

// Custom plugin to display percentage text on top of bars
const dataLabelsPlugin = {
  id: "dataLabels",
  afterDatasetsDraw: (chart) => {
    const {
      ctx,
      chartArea: { top },
      data,
      scales: { x, y },
    } = chart;

    ctx.save();
    ctx.font = "12px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = "#444";

    data.datasets.forEach((dataset, datasetIndex) => {
      dataset.data.forEach((value, index) => {
        const xPos = x.getPixelForValue(index);
        const yPos = y.getPixelForValue(value);
        // Draw text above the bar with a 5px offset
        ctx.fillText(`${value}%`, xPos, yPos - 5);
      });
    });

    ctx.restore();
  },
};

// Custom plugin to draw company names under bar groups
const groupLabelsPlugin = {
  id: "groupLabels",
  afterDatasetsDraw: (chart) => {
    const {
      ctx,
      scales: { x },
      chartArea: { bottom },
    } = chart;

    ctx.save();
    ctx.font = "14px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#444";

    // Define group labels and their positions
    const groups = [
      { name: "Constructor", startIndex: 0, endIndex: 2 },
      { name: "Engineering", startIndex: 3, endIndex: 5 },
      { name: "I&E", startIndex: 6, endIndex: 8 },
    ];

    groups.forEach((group) => {
      // Calculate the center position for the group label
      const startX = x.getPixelForValue(group.startIndex);
      const endX = x.getPixelForValue(group.endIndex);
      const centerX = (startX + endX) / 2;
      // Draw the label below the chart (adjust offset as needed)
      ctx.fillText(group.name, centerX, bottom + 10);
    });

    ctx.restore();
  },
};

const CompanyAttendanceGrouped = ({ companysummary }) => {
  const companyOrder = [
    "CONSTRUCTOR",
    "ENGINEERING",
    "INSTRUMENTATION & ELECTRICAL (I&E)",
  ];

  // Flatten the data values in the expected sequence
  const attendanceData = companyOrder.flatMap((name) => {
    const company = companysummary.find((c) => c.company_name === name);
    return company ? [company.on_time, company.late, company.absent] : [0, 0, 0];
  });

  const data = {
    labels: [
      "Constructor - On Time",
      "Constructor - Late",
      "Constructor - Absent",
      "Engineering - On Time",
      "Engineering - Late",
      "Engineering - Absent",
      "I&E - On Time",
      "I&E - Late",
      "I&E - Absent",
    ],
    datasets: [
      {
        label: "Attendance",
        data: attendanceData,
        backgroundColor: [
          "#F54900",
          "#009689",
          "#104E64",
          "#F54900",
          "#009689",
          "#104E64",
          "#F54900",
          "#009689",
          "#104E64",
        ],
        barThickness: 25,
        // Explicitly disable datalabels for this dataset
        datalabels: {
          display: false,
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: { display: false },
        grid: { drawTicks: false, drawBorder: false },
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 25,
          callback: (value) => `${value}%`,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.parsed.y}%`,
        },
      },
      // Explicitly disable datalabels plugin
      datalabels: {
        display: false,
      },
    },
    layout: {
      padding: {
        top: 20, // Increased top padding for data labels
        bottom: 80, // Increased bottom padding to accommodate group labels
      },
    },
  };

  return (
    <div
      className="w-full max-w-[900px] h-[400px] mx-auto p-6 rounded-[12.75px] border border-black/10 bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),_0px_1px_2px_-1px_rgba(0,0,0,0.10)]"
      style={{ position: "relative", overflow: "visible" }}
    >
      {/* Top row with title and dots */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">Company Attendance</h1>
          <div className="text-sm text-[#717182] mt-1">
            Attendance metrics by company
          </div>
        </div>

        {/* Dot Legends on top right */}
        <div className="flex items-center gap-5 pr-2">
          <div className="flex items-center gap-2">
            <span
              style={{
                width: 10,
                height: 10,
                backgroundColor: "#F54900",
                borderRadius: "50%",
                display: "inline-block",
              }}
            />
            <span className="text-sm text-[#444]">On Time</span>
          </div>

          <div className="flex items-center gap-2">
            <span
              style={{
                width: 10,
                height: 10,
                backgroundColor: "#009689",
                borderRadius: "50%",
                display: "inline-block",
              }}
            />
            <span className="text-sm text-[#444]">Late</span>
          </div>

          <div className="flex items-center gap-2">
            <span
              style={{
                width: 10,
                height: 10,
                backgroundColor: "#104E64",
                borderRadius: "50%",
                display: "inline-block",
              }}
            />
            <span className="text-sm text-[#444]">Absent</span>
          </div>
        </div>
      </div>

      {/* Chart with bottom legends */}
      <div style={{ position: "relative", height: "calc(100% - 60px)" }}>
        <Bar
          data={data}
          options={options}
          plugins={[dataLabelsPlugin, groupLabelsPlugin]}
        />

        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            gap: 30,
            fontSize: 14,
            color: "#444",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: "#F54900",
                borderRadius: 3,
              }}
            />
            <span>On Time</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: "#009689",
                borderRadius: 3,
              }}
            />
            <span>Late</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: "#104E64",
                borderRadius: 3,
              }}
            />
            <span>Absent</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyAttendanceGrouped;